import { api } from "./api";

export interface MatchCriteria {
  valor: string;
  data: string;
  nif: string;
  descricao: string;
}

export interface Invoice {
  id: number;
  numero_fatura: string;
  nif_emitente: string;
  nif_adquirente: string;
  valor: string;
  data_emissao: string;
  categoria: string;
  tipo?: string;
  descricao?: string;
  hash_validacao?: string;
  mes_relatorio?: string;
}

export interface BankTransaction {
  id: number;
  bank_statement_id: number;
  data_movimento: string;
  data_valor: string;
  descricao: string;
  valor: string;
  saldo: string;
  categoria?: string;
}

export interface Match {
  id: number;
  invoice_id: number;
  transaction_id: number;
  match_type: "automatic" | "manual" | "suggested";
  confidence_score: number;
  status: "pending" | "approved" | "rejected";
  match_criteria: MatchCriteria;
  amount_matched: number;
  notes?: string;
  matched_by: string;
  matched_at: string;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  numero_fatura?: string;
  nif_emitente?: string;
  invoice_valor?: string;
  data_emissao?: string;
  invoice_categoria?: string;
  invoice_descricao?: string;
  transaction_descricao?: string;
  transaction_valor?: string;
  data_movimento?: string;
  data_valor?: string;
  saldo?: string;
}

export interface MatchStats {
  invoices: {
    total: number;
    matched: number;
    unmatched: number;
    match_percentage: string;
    total_value: number;
    matched_value: number;
  };
  transactions: {
    total: number;
    matched: number;
    unmatched: number;
  };
  matches: {
    by_status: {
      pending: number;
      approved: number;
      rejected: number;
    };
    by_type: {
      automatic: number;
      manual: number;
      suggested: number;
    };
    avg_confidence_score: string;
  };
}

export interface MatchSuggestion {
  invoice?: Invoice;
  transaction?: BankTransaction;
  suggestions: Array<{
    invoice?: Invoice;
    transaction?: BankTransaction;
    score: number;
    criteria: MatchCriteria;
  }>;
}

export interface AutoMatchingOptions {
  dryRun?: boolean;
  threshold?: number;
  autoApproveThreshold?: number;
  days?: number;
}

export interface AutoMatchingResult {
  success: boolean;
  dry_run: boolean;
  execution_time_ms: number;
  summary: {
    total_invoices: number;
    total_transactions: number;
    matches_suggested: number;
    matches_created: number;
    auto_approved: number;
    pending_approval: number;
    avg_confidence_score: number;
  };
  suggestions?: any[];
}

export interface PaginatedResponse<T> {
  data?: T[];
  matches?: T[];
  invoices?: T[];
  transactions?: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const matchingService = {
  /**
   * Run automatic matching algorithm
   */
  async runAutoMatching(
    options: AutoMatchingOptions = {}
  ): Promise<AutoMatchingResult> {
    const response = await api.post(`/matches/auto-run`, {
      dryRun: options.dryRun || false,
      threshold: options.threshold || 70,
      autoApproveThreshold: options.autoApproveThreshold || 90,
      days: options.days || 90,
    });
    return response.data;
  },

  /**
   * Get matching statistics
   */
  async getStats(filters?: {
    start_date?: string;
    end_date?: string;
  }): Promise<MatchStats> {
    const response = await api.get(`/matches/stats`, {
      params: filters,
    });
    return response.data;
  },

  /**
   * List matches with filters
   */
  async listMatches(filters?: {
    page?: number;
    limit?: number;
    status?: string;
    match_type?: string;
    min_score?: number;
    max_score?: number;
    invoice_id?: number;
    transaction_id?: number;
  }): Promise<PaginatedResponse<Match>> {
    const response = await api.get(`/matches`, {
      params: filters,
    });
    return response.data;
  },

  /**
   * Get match by ID
   */
  async getMatch(id: number): Promise<Match> {
    const response = await api.get(`/matches/${id}`);
    return response.data;
  },

  /**
   * Create manual match
   */
  async createMatch(data: {
    invoice_id: number;
    transaction_id: number;
    amount_matched?: number;
    notes?: string;
    matched_by?: string;
  }): Promise<Match> {
    const response = await api.post(`/matches`, data);
    return response.data;
  },

  /**
   * Approve a match
   */
  async approveMatch(id: number, approved_by: string = "user"): Promise<Match> {
    const response = await api.patch(`/matches/${id}/approve`, {
      approved_by,
    });
    return response.data;
  },

  /**
   * Reject a match
   */
  async rejectMatch(id: number): Promise<Match> {
    const response = await api.patch(`/matches/${id}/reject`);
    return response.data;
  },

  /**
   * Delete a match
   */
  async deleteMatch(id: number): Promise<void> {
    await api.delete(`/matches/${id}`);
  },

  /**
   * Get match suggestions for an invoice
   */
  async getSuggestionsForInvoice(
    invoiceId: number,
    options?: { limit?: number; days?: number; threshold?: number }
  ): Promise<MatchSuggestion> {
    const response = await api.get(
      `/matches/suggestions/invoice/${invoiceId}`,
      { params: options }
    );
    return response.data;
  },

  /**
   * Get match suggestions for a transaction
   */
  async getSuggestionsForTransaction(
    transactionId: number,
    options?: { limit?: number; threshold?: number }
  ): Promise<MatchSuggestion> {
    const response = await api.get(
      `/matches/suggestions/transaction/${transactionId}`,
      { params: options }
    );
    return response.data;
  },

  /**
   * Get unmatched invoices
   */
  async getUnmatchedInvoices(filters?: {
    page?: number;
    limit?: number;
    categoria?: string;
    tipo?: string;
    mes_relatorio?: string;
  }): Promise<PaginatedResponse<Invoice>> {
    const response = await api.get(`/invoices/unmatched`, {
      params: filters,
    });
    return response.data;
  },

  /**
   * Get unmatched transactions
   */
  async getUnmatchedTransactions(filters?: {
    page?: number;
    limit?: number;
    days?: number;
  }): Promise<PaginatedResponse<BankTransaction>> {
    const response = await api.get(
      `/bank-statements/transactions/unmatched`,
      {
        params: filters,
      }
    );
    return response.data;
  },
};

export default matchingService;
